import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const ApiDocumentation = () => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('companies');
  const [expandedEndpoint, setExpandedEndpoint] = useState(null);

  const apiCategories = {
    companies: {
      name: t('api.companies'),
      icon: '🏢',
      endpoints: [
        {
          method: 'GET',
          path: '/api/companies',
          description: t('api.getCompaniesDesc'),
          auth: true,
          permission: 'companies:view',
          response: {
            type: 'Array',
            example: '[{ _id, name, address, city, postalCode, email, phone, ... }]'
          }
        },
        {
          method: 'GET',
          path: '/api/companies/:id',
          description: t('api.getCompanyDesc'),
          auth: true,
          permission: 'companies:view',
          params: { id: 'Company ID' },
          response: {
            type: 'Object',
            example: '{ _id, name, address, ... }'
          }
        },
        {
          method: 'POST',
          path: '/api/companies',
          description: t('api.createCompanyDesc'),
          auth: true,
          permission: 'companies:create',
          body: {
            name: 'string (required)',
            address: 'string (required)',
            city: 'string (required)',
            postalCode: 'string (required)',
            email: 'string (required)',
            phone: 'string (required)',
            taxId: 'string (optional)',
            vatId: 'string (optional)'
          },
          response: {
            type: 'Object',
            example: '{ message, company }'
          }
        },
        {
          method: 'PUT',
          path: '/api/companies/:id',
          description: t('api.updateCompanyDesc'),
          auth: true,
          permission: 'companies:update',
          params: { id: 'Company ID' },
          body: {
            name: 'string',
            address: 'string',
            city: 'string',
            postalCode: 'string',
            email: 'string',
            phone: 'string'
          },
          response: {
            type: 'Object',
            example: '{ message, company }'
          }
        },
        {
          method: 'DELETE',
          path: '/api/companies/:id',
          description: t('api.deleteCompanyDesc'),
          auth: true,
          permission: 'companies:delete',
          params: { id: 'Company ID' },
          response: {
            type: 'Object',
            example: '{ message }'
          }
        }
      ]
    },
    employees: {
      name: t('api.employees'),
      icon: '👷',
      endpoints: [
        {
          method: 'GET',
          path: '/api/employees',
          description: t('api.getEmployeesDesc'),
          auth: true,
          permission: 'employees:view',
          response: {
            type: 'Array',
            example: '[{ _id, firstName, lastName, email, jobTitle, ... }]'
          }
        },
        {
          method: 'POST',
          path: '/api/employees',
          description: t('api.createEmployeeDesc'),
          auth: true,
          permission: 'employees:create',
          body: {
            companyId: 'ObjectId (required)',
            title: 'string: Herr/Frau (required)',
            firstName: 'string (required)',
            lastName: 'string (required)',
            email: 'string (optional)',
            birthDate: 'date (required)',
            birthPlace: 'string (required)',
            maritalStatus: 'string: Ledig/Verheiratet/Geschieden/Verwitwet (required)',
            nationality: 'string (required)',
            address: 'string (required)',
            postalCode: 'string (required)',
            city: 'string (required)',
            employmentStartDate: 'date (required)',
            employmentEndDate: 'date (optional)',
            jobTitle: 'string (required)',
            salaryType: 'string: monthly/hourly (required)',
            salaryAmount: 'number (required)',
            weeklyWorkingHours: 'number 1-60 (required)'
          }
        },
        {
          method: 'PUT',
          path: '/api/employees/:id',
          description: t('api.updateEmployeeDesc'),
          auth: true,
          permission: 'employees:update',
          params: { id: 'Employee ID' }
        },
        {
          method: 'DELETE',
          path: '/api/employees/:id',
          description: t('api.deleteEmployeeDesc'),
          auth: true,
          permission: 'employees:delete',
          params: { id: 'Employee ID' }
        }
      ]
    },
    customers: {
      name: t('api.customers'),
      icon: '👥',
      endpoints: [
        {
          method: 'GET',
          path: '/api/customers',
          description: t('api.getCustomersDesc'),
          auth: true,
          permission: 'customers:view'
        },
        {
          method: 'POST',
          path: '/api/customers',
          description: t('api.createCustomerDesc'),
          auth: true,
          permission: 'customers:create',
          body: {
            name: 'string (required)',
            email: 'string (required)',
            address: 'string (required)',
            city: 'string (required)',
            postalCode: 'string (required)',
            phone: 'string (optional)'
          }
        }
      ]
    },
    standardDocuments: {
      name: t('api.standardDocuments'),
      icon: '📋',
      endpoints: [
        {
          method: 'GET',
          path: '/api/standard-documents',
          description: t('api.getStandardDocumentsDesc'),
          auth: true,
          permission: 'standardDocuments:view',
          query: {
            page: 'number (default: 1)',
            limit: 'number (default: 10)',
            documentType: 'string (optional filter)'
          }
        },
        {
          method: 'POST',
          path: '/api/standard-documents',
          description: t('api.createStandardDocumentDesc'),
          auth: true,
          permission: 'standardDocuments:create',
          body: {
            companyId: 'ObjectId (required)',
            employeeId: 'ObjectId (required)',
            documentType: 'string: arbeitgeberbescheinigung | außerordentliche_kündigung | kündigung_betriebliche_gründe | beendigung_vertragsablauf | fristgerechte_kündigung | arbeitsaufforderung (required)',
            documentName: 'string (required)',
            senderName: 'string (for termination docs)',
            note: 'string (optional)',
            terminationDate: 'date (for termination docs)',
            employmentStartDate: 'date (for fristgerechte_kündigung)',
            employmentLeftoverVacationDays: 'string (for kündigung_betriebliche_gründe)',
            absenceStartDate: 'date (for arbeitsaufforderung)',
            absenceDeadlineDate: 'date (for arbeitsaufforderung)'
          }
        },
        {
          method: 'POST',
          path: '/api/standard-documents/:id/generate-pdf',
          description: t('api.generatePdfDesc'),
          auth: true,
          permission: 'standardDocuments:view',
          params: { id: 'Document ID' },
          query: { withSignature: 'boolean (true/false)' }
        },
        {
          method: 'POST',
          path: '/api/standard-documents/:id/send-mail',
          description: t('api.sendDocumentEmailDesc'),
          auth: true,
          permission: 'invoices:view',
          params: { id: 'Document ID' },
          body: {
            smtpId: 'ObjectId (required)',
            toEmail: 'string (required)',
            toName: 'string (optional)',
            subject: 'string (if not using template)',
            body: 'string (if not using template)',
            templateId: 'ObjectId (if using template)'
          }
        }
      ]
    },
    invoices: {
      name: t('api.invoices'),
      icon: '📄',
      endpoints: [
        {
          method: 'GET',
          path: '/api/invoices',
          description: t('api.getInvoicesDesc'),
          auth: true,
          permission: 'invoices:view',
          query: {
            page: 'number',
            limit: 'number',
            status: 'draft | sent | paid | unpaid | void',
            companyId: 'ObjectId'
          }
        },
        {
          method: 'POST',
          path: '/api/invoices',
          description: t('api.createInvoiceDesc'),
          auth: true,
          permission: 'invoices:create'
        },
        {
          method: 'POST',
          path: '/api/invoices/:id/regenerate-pdf',
          description: t('api.regeneratePdfDesc'),
          auth: true,
          permission: 'invoices:view',
          params: { id: 'Invoice ID' }
        }
      ]
    },
    incomingInvoices: {
      name: t('api.incomingInvoices'),
      icon: '📥',
      endpoints: [
        {
          method: 'GET',
          path: '/api/incoming-invoices',
          description: t('api.getIncomingInvoicesDesc'),
          auth: true,
          permission: 'incomingInvoices:view',
          query: {
            page: 'number',
            limit: 'number',
            status: 'unpaid | paid | cancelled'
          },
          response: {
            type: 'Object',
            example: '{ invoices: [...], pagination: { page, limit, total, pages } }'
          }
        },
        {
          method: 'GET',
          path: '/api/incoming-invoices/statistics',
          description: t('api.getIncomingInvoicesStatsDesc'),
          auth: true,
          permission: 'incomingInvoices:view',
          response: {
            type: 'Object',
            example: '{ unpaid: 5, paid: 10, cancelled: 1, totalAmount: 50000, paymentAmount: 30000, outstandingAmount: 20000, monthlyBreakdown: [...], yearlyBreakdown: [...], upcomingPayments: [...] }'
          }
        },
        {
          method: 'POST',
          path: '/api/incoming-invoices',
          description: t('api.createIncomingInvoiceDesc'),
          auth: true,
          permission: 'incomingInvoices:create',
          body: {
            serialNumber: 'string (optional)',
            bookingDate: 'Date (required)',
            documentDate: 'Date (required)',
            documentNumber: 'string (required)',
            customer: 'string (required)',
            purpose: 'string (optional)',
            grossAmount: 'number (required)',
            reminderFees: 'number (optional)',
            dueDate: 'Date (optional)',
            paymentAmount: 'number (optional)',
            paidOn: 'Date (optional)',
            status: 'unpaid | paid | cancelled (default: unpaid)'
          }
        },
        {
          method: 'PUT',
          path: '/api/incoming-invoices/:id',
          description: t('api.updateIncomingInvoiceDesc'),
          auth: true,
          permission: 'incomingInvoices:update',
          params: { id: 'Incoming Invoice ID' },
          body: {
            serialNumber: 'string (optional)',
            bookingDate: 'Date (optional)',
            documentDate: 'Date (optional)',
            documentNumber: 'string (optional)',
            customer: 'string (optional)',
            purpose: 'string (optional)',
            grossAmount: 'number (optional)',
            reminderFees: 'number (optional)',
            dueDate: 'Date (optional)',
            paymentAmount: 'number (optional)',
            paidOn: 'Date (optional)',
            status: 'unpaid | paid | cancelled (optional)'
          }
        },
        {
          method: 'DELETE',
          path: '/api/incoming-invoices/:id',
          description: t('api.deleteIncomingInvoiceDesc'),
          auth: true,
          permission: 'incomingInvoices:delete',
          params: { id: 'Incoming Invoice ID' }
        }
      ]
    },
    smtp: {
      name: t('api.smtp'),
      icon: '📧',
      endpoints: [
        {
          method: 'GET',
          path: '/api/smtp-settings',
          description: t('api.getSmtpSettingsDesc'),
          auth: true
        },
        {
          method: 'POST',
          path: '/api/smtp-settings',
          description: t('api.createSmtpDesc'),
          auth: true,
          body: {
            name: 'string (required)',
            host: 'string (required)',
            port: 'number (required)',
            secure: 'boolean',
            username: 'string (required)',
            password: 'string (required)',
            fromEmail: 'string (required)',
            fromName: 'string (required)',
            isDefault: 'boolean',
            isActive: 'boolean'
          }
        },
        {
          method: 'POST',
          path: '/api/smtp-settings/:id/test',
          description: t('api.testSmtpDesc'),
          auth: true,
          params: { id: 'SMTP ID' }
        }
      ]
    },
    mailTemplates: {
      name: t('api.mailTemplates'),
      icon: '📝',
      endpoints: [
        {
          method: 'GET',
          path: '/api/mail-templates',
          description: t('api.getMailTemplatesDesc'),
          auth: true
        },
        {
          method: 'POST',
          path: '/api/mail-templates',
          description: t('api.createMailTemplateDesc'),
          auth: true,
          body: {
            name: 'string (required)',
            subject: 'string (required)',
            body: 'string (required)',
            isDefault: 'boolean'
          }
        },
        {
          method: 'GET',
          path: '/api/mail-templates/variables',
          description: t('api.getTemplateVariablesDesc'),
          auth: true,
          response: {
            type: 'Object',
            example: '{ documentVariables: [...], employeeVariables: [...], companyVariables: [...] }'
          }
        },
        {
          method: 'PUT',
          path: '/api/mail-templates/:id',
          description: t('api.updateMailTemplateDesc'),
          auth: true,
          params: { id: 'Template ID' }
        },
        {
          method: 'DELETE',
          path: '/api/mail-templates/:id',
          description: t('api.deleteMailTemplateDesc'),
          auth: true,
          params: { id: 'Template ID' }
        }
      ]
    },
    users: {
      name: t('api.users'),
      icon: '👤',
      endpoints: [
        {
          method: 'GET',
          path: '/api/users',
          description: t('api.getUsersDesc'),
          auth: true,
          permission: 'users:view',
          response: {
            type: 'Array',
            example: '[{ _id, name, email, permissionGroupId, isActive, ... }]'
          }
        },
        {
          method: 'POST',
          path: '/api/users',
          description: t('api.createUserDesc'),
          auth: true,
          permission: 'users:create',
          body: {
            name: 'string (required)',
            email: 'string (required)',
            password: 'string (required)',
            permissionGroupId: 'ObjectId (required)',
            isActive: 'boolean (default: true)'
          }
        },
        {
          method: 'PUT',
          path: '/api/users/:id',
          description: t('api.updateUserDesc'),
          auth: true,
          permission: 'users:update',
          params: { id: 'User ID' },
          body: {
            name: 'string',
            email: 'string',
            permissionGroupId: 'ObjectId',
            isActive: 'boolean',
            passwordHash: 'string (optional - to change password)'
          }
        },
        {
          method: 'DELETE',
          path: '/api/users/:id',
          description: t('api.deleteUserDesc'),
          auth: true,
          permission: 'users:delete',
          params: { id: 'User ID' }
        }
      ]
    },
    permissionGroups: {
      name: t('api.permissionGroups'),
      icon: '🔐',
      endpoints: [
        {
          method: 'GET',
          path: '/api/permission-groups',
          description: t('api.getPermissionGroupsDesc'),
          auth: true,
          permission: 'permissionGroups:view',
          response: {
            type: 'Array',
            example: '[{ _id, name, permissions: { companies: {...}, employees: {...}, ... } }]'
          }
        },
        {
          method: 'GET',
          path: '/api/permission-groups/:id',
          description: t('api.getPermissionGroupDesc'),
          auth: true,
          permission: 'permissionGroups:view',
          params: { id: 'Permission Group ID' }
        },
        {
          method: 'POST',
          path: '/api/permission-groups',
          description: t('api.createPermissionGroupDesc'),
          auth: true,
          permission: 'permissionGroups:create',
          body: {
            name: 'string (required)',
            permissions: 'object with module permissions (required)'
          }
        },
        {
          method: 'PUT',
          path: '/api/permission-groups/:id',
          description: t('api.updatePermissionGroupDesc'),
          auth: true,
          permission: 'permissionGroups:update',
          params: { id: 'Permission Group ID' }
        },
        {
          method: 'DELETE',
          path: '/api/permission-groups/:id',
          description: t('api.deletePermissionGroupDesc'),
          auth: true,
          permission: 'permissionGroups:delete',
          params: { id: 'Permission Group ID' }
        }
      ]
    },
    settings: {
      name: t('api.settings'),
      icon: '⚙️',
      endpoints: [
        {
          method: 'GET',
          path: '/api/settings',
          description: t('api.getSettingsDesc'),
          auth: true,
          response: {
            type: 'Object',
            example: '{ systemName, currency, dateFormat, defaultLocale, ... }'
          }
        },
        {
          method: 'PUT',
          path: '/api/settings',
          description: t('api.updateSettingsDesc'),
          auth: true,
          permission: 'settings:update',
          body: {
            systemName: 'string',
            currency: 'string: EUR | USD | TRY',
            dateFormat: 'string: DD.MM.YYYY | MM/DD/YYYY | YYYY-MM-DD',
            defaultLocale: 'string: en | de | tr | ru',
            taxRate: 'number'
          }
        }
      ]
    },
    authentication: {
      name: t('api.authEndpoints'),
      icon: '🔑',
      endpoints: [
        {
          method: 'POST',
          path: '/api/auth/login',
          description: t('api.loginDesc'),
          auth: false,
          body: {
            email: 'string (required)',
            password: 'string (required)'
          },
          response: {
            type: 'Object',
            example: '{ token, user: { id, name, email, permissionGroupId } }'
          }
        },
        {
          method: 'POST',
          path: '/api/auth/register',
          description: t('api.registerDesc'),
          auth: false,
          body: {
            name: 'string (required)',
            email: 'string (required)',
            password: 'string (required)'
          }
        },
        {
          method: 'GET',
          path: '/api/auth/me',
          description: t('api.getMeDesc'),
          auth: true,
          response: {
            type: 'Object',
            example: '{ id, name, email, permissionGroupId, permissions }'
          }
        }
      ]
    }
  };

  const toggleEndpoint = (index) => {
    setExpandedEndpoint(expandedEndpoint === index ? null : index);
  };

  const getMethodColor = (method) => {
    switch (method) {
      case 'GET': return 'bg-green-100 text-green-800 border-green-300';
      case 'POST': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'PUT': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'DELETE': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t('api.title')}
          </h1>
          <p className="text-gray-600">{t('api.description')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Categories Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sticky top-4">
              <h3 className="text-lg font-semibold mb-4 text-gray-900">{t('api.categories')}</h3>
              <nav className="space-y-2">
                {Object.entries(apiCategories).map(([key, category]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedCategory(key)}
                    className={`w-full flex items-center px-3 py-2 rounded-lg text-left transition-colors ${
                      selectedCategory === key
                        ? 'bg-primary-100 text-primary-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <span className="mr-2 text-xl">{category.icon}</span>
                    {category.name}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Endpoints List */}
          <div className="lg:col-span-3">
            <div className="space-y-4">
              {apiCategories[selectedCategory]?.endpoints.map((endpoint, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
                >
                  {/* Endpoint Header */}
                  <button
                    onClick={() => toggleEndpoint(index)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <span className={`px-3 py-1 rounded-md text-sm font-semibold border ${getMethodColor(endpoint.method)}`}>
                        {endpoint.method}
                      </span>
                      <code className="text-sm font-mono text-gray-900">{endpoint.path}</code>
                    </div>
                    <svg
                      className={`w-5 h-5 text-gray-400 transition-transform ${
                        expandedEndpoint === index ? 'transform rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Endpoint Details */}
                  {expandedEndpoint === index && (
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                      {/* Description */}
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">{t('api.description')}</h4>
                        <p className="text-gray-600">{endpoint.description}</p>
                      </div>

                      {/* Authentication */}
                      {endpoint.auth && (
                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">{t('api.authentication')}</h4>
                          <div className="flex items-center space-x-2">
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
                              Bearer Token
                            </span>
                            <code className="text-xs text-gray-600">Authorization: Bearer YOUR_TOKEN</code>
                          </div>
                        </div>
                      )}

                      {/* Permission */}
                      {endpoint.permission && (
                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">{t('api.requiredPermission')}</h4>
                          <code className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">
                            {endpoint.permission}
                          </code>
                        </div>
                      )}

                      {/* URL Parameters */}
                      {endpoint.params && (
                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">{t('api.urlParameters')}</h4>
                          <div className="bg-white rounded border border-gray-200 p-3">
                            {Object.entries(endpoint.params).map(([key, value]) => (
                              <div key={key} className="flex justify-between py-1">
                                <code className="text-sm text-blue-600">{key}</code>
                                <span className="text-sm text-gray-600">{value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Query Parameters */}
                      {endpoint.query && (
                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">{t('api.queryParameters')}</h4>
                          <div className="bg-white rounded border border-gray-200 p-3">
                            {Object.entries(endpoint.query).map(([key, value]) => (
                              <div key={key} className="flex justify-between py-1">
                                <code className="text-sm text-blue-600">{key}</code>
                                <span className="text-sm text-gray-600">{value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Request Body */}
                      {endpoint.body && (
                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">{t('api.requestBody')}</h4>
                          <div className="bg-gray-900 rounded p-4 overflow-x-auto">
                            <pre className="text-green-400 text-xs font-mono">
{JSON.stringify(endpoint.body, null, 2)}
                            </pre>
                          </div>
                        </div>
                      )}

                      {/* Response */}
                      {endpoint.response && (
                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">{t('api.response')}</h4>
                          <div className="bg-gray-900 rounded p-4 overflow-x-auto">
                            <pre className="text-green-400 text-xs font-mono">
{endpoint.response.example}
                            </pre>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiDocumentation;

